import React, {useState, useEffect} from "react";
import {
    Typography,
    Grid,
    TextField as RTextField,
    Box,
    Button,
    makeStyles,
    Container,
} from "@material-ui/core";
import {TextField} from "formik-material-ui";
import MuiPhoneNumber from "material-ui-phone-number";
import {Formik, Form, Field, ErrorMessage} from "formik";
import {colorError,} from "../../../Constants/theme";
import * as Yup from "yup";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";
import moment from "moment";
import {api_getUserLocation} from "../../../redux/user/user.api";
import { useSnackbar } from "notistack";
import publicIp from "public-ip";
import {createUserProfile, getUserProfile, patchUserProfile} from "../../../redux/userProfile/userProfile.actions";
import { DatePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import {postReferralCode} from "../../../redux/user/user.actions";
import withLocation from "../../../hocs/withLocation";
import {foundUsOptions} from "../../../Constants/foundUsOptions";
import MenuItem from "@material-ui/core/MenuItem";
import DateFnsUtils from "@date-io/date-fns";
interface Props {
	changeCount: (number: number) => void;
	getLocation: () => void;
	hasSubscription: any;
}

const useStyle = makeStyles(() => ({
    body: {
        paddingTop: 20,
    },
    spacing: {
        paddingTop: 20,
    },
    cancelButton: {
        border: `1.5px solid ${colorError}`,
        color: colorError
    },
    hidden: {
        display: "none"
    },
    formError: {
        color: colorError,
        fontSize: "12px",
        marginLeft: "14px",
        marginTop: "4px"
    },
    applyBtn: {
        height: 56
    },
}));

interface IProfile {
	firstName: string,
	lastName: string,
	phoneNumber: string,
	practiceCode: string,
	userId: number,
	type: string,
	locationTimeZone: string,
	stateOfResidence?: string,
	birthday?: string,
    findUs: string,
}

const Profile = ({changeCount, getLocation, hasSubscription}: Props) => {
    const classes = useStyle();
    const { enqueueSnackbar } = useSnackbar();
    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required("Required"),
        lastName: Yup.string().required("Required"),
        findUs: Yup.string().required("Required"),
        birthday: Yup.string().required("Required"),
        phoneNumber: Yup.string().length(14, "Invalid Phone Number").required("Required"),
    });
    const user = useSelector((state: RootState) => state?.user);
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const dispatch = useAppDispatch();
    const today = moment();
    const [formikValues, setFormikValues] = useState({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        findUs: isWhiteLabel ? "veterinarian" : "",
        birthday: "",
    });
    const [profileId, setProfileId] = useState(0);
    const [referralCode, setReferralCode] = useState("");
    const [referralCodeApplied, setReferralCodeApplied] = useState(false);
    const [applyButtonDisabled, setApplyButtonDisabled] = useState(true);
    const [isSubmit, setIsSubmit] = useState(false);
    const [isPhone, setIsPhone] = useState(false);
    const [isBirthday, setIsBirthday] = useState(false);

    const applyReferralCode = async (code) => {
        try {
            const referralCodeRequest = await dispatch(postReferralCode(code));
            setReferralCodeApplied(true);
            enqueueSnackbar(`$${referralCodeRequest.discountAmount.toFixed(2)} applied to your account`, {
                autoHideDuration: 4000
            });
        } catch (err) {
            enqueueSnackbar("Invalid referral code", {
                variant: "error",
                autoHideDuration: 4000
            });
        }
    };

    useEffect(() => {
        if(referralCode.length) {
            setApplyButtonDisabled(false);
        } else {
            setApplyButtonDisabled(true);
        }
    }, [referralCode]);

    useEffect(() => {
        getLocation();
    }, []);

    useEffect(() => {
        dispatch(getUserProfile({userId: user.userId})).then((profile) => {
            // Presetting fields with data from BE
            if (profile.length) {
                setProfileId(profile[0].id);
                const newFormikValues = {...formikValues};
                newFormikValues.firstName = profile[0]?.firstName || "";
                newFormikValues.lastName = profile[0].lastName || "";
                newFormikValues.phoneNumber = profile[0].phoneNumber || "";
                newFormikValues.findUs = profile[0].findUs || "";
                newFormikValues.birthday = profile[0].birthday || moment().format("YYYY-MM-DD");
                setFormikValues({...newFormikValues});
            }
        }).catch(err => enqueueSnackbar(err.errorMessage, {
            variant: "error",
            autoHideDuration: 4000
        }));
    },[]);

    return (
        <Container className={classes.body} disableGutters={true}>
            <Typography variant="h2" gutterBottom>
                Your Profile
            </Typography>
            <Formik
                initialValues={formikValues}
                enableReinitialize
                onSubmit={async (values: any) => {
                    try {
                        const ip = await publicIp.v4();
                        const location = await api_getUserLocation(ip);
                        const userId = user.userId;
                        const type = "owner";
                        const state = location?.country_code === "US" ? location?.region_code : "";
                        let body: IProfile = {
                            firstName: values.firstName,
                            lastName: values.lastName,
                            phoneNumber: values.phoneNumber,
                            findUs: isWhiteLabel ? "veterinarian" : values.findUs,
                            practiceCode: "01",
                            birthday: values.birthday,
                            userId,
                            type,
                            locationTimeZone: moment.tz.guess()
                        };
                        if(state) {
                            body.stateOfResidence = state;
                        }
                        if(profileId !== 0){
                            await dispatch(patchUserProfile({id: profileId, body: body}));
                        } else {
                            await dispatch(createUserProfile(body));
                        }
                        changeCount(1);
                    } catch (error) {
                        enqueueSnackbar(error.errorMessage, {
                            variant: "error",
                            autoHideDuration: 3000,
                        });
                    }
                }}
                validationSchema={validationSchema}
            >
                {({isSubmitting, errors, values, handleChange, setFieldValue, enableReinitialize=true}) => (
                    <Form>
                        <Grid container>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    First Name
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <Field
                                    id="firstName"
                                    label="First Name"
                                    name="firstName"
                                    component={TextField}
                                    variant="outlined"
                                    color="secondary"
                                    type="text"
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    onChange={handleChange}
                                    value={values.firstName}
                                    placeholder=""
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    Last Name
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <Field
                                    id="lastName"
                                    name="lastName"
                                    label="Last Name"
                                    component={TextField}
                                    variant="outlined"
                                    color="secondary"
                                    type="text"
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    onChange={handleChange}
                                    value={values.lastName}
                                    placeholder=""
                                />
                            </Grid>
                        </Grid>
                        <Grid container className={classes.spacing}>
                            <Grid item xs={12}>
                                <Typography variant="h3" gutterBottom>
                                    Phone Number
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography gutterBottom>
									We recommend using a cell phone number so that you can receive SMS appointment reminders and follow up check ins!
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <MuiPhoneNumber
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    defaultCountry={"us"}
                                    onlyCountries={["us"]}
                                    error={(isSubmit || isPhone) && values.phoneNumber.length !== 14}
                                    onChange={(value) => {
                                        setFieldValue("phoneNumber", value);
                                        setIsPhone(false);
                                    }}
                                    onBlur={() => {
                                        if (!values.phoneNumber.length) {
                                            setFieldValue("phoneNumber", "");
                                            setIsPhone(true);
                                        }
                                    }}
                                    value={values.phoneNumber}
                                    disableCountryCode={true}
                                    variant="outlined"
                                    fullWidth
                                />
                                {(values.phoneNumber.length !== 14 && (isSubmit || isPhone)) && <Typography
                                    className={classes.formError}>{values.phoneNumber.length !== 14 ? "Invalid Phone Number" : "Required"}</Typography>}
                            </Grid>
                        </Grid>
                        <Grid container className={classes.spacing}>
                            <Grid item xs={12}>
                                <Typography variant="h3" gutterBottom>
                                    Birthday
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography gutterBottom>
									This is required by law so that the veterinarian can confirm your identity during the appointment.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <DatePicker
                                        defaultValue={moment().format("YYYY-MM-DD")}
                                        inputVariant="outlined"
                                        disableFuture={true}
                                        error={(isSubmit || isBirthday) && !values.birthday}
                                        fullWidth
                                        label="Birthday"
                                        format="MMM dd, yyyy"
                                        openTo="year"
                                        views={["year", "month", "date"]}
                                        value={values.birthday || null}
                                        onChange={(value) => {
                                            setFieldValue("birthday", moment(value).format("YYYY-MM-DD"));
                                            setIsBirthday(false);
                                        }}
                                        onBlur={() => {
                                            if (!values.birthday.length) {
                                                setFieldValue("birthday", "");
                                                setIsBirthday(true);
                                            }
                                        }}
                                    />
                                </MuiPickersUtilsProvider>
                                {(!values.birthday && (isBirthday || isSubmit)) && <Typography
                                    className={classes.formError}>Required</Typography>}
                            </Grid>
                            {!isWhiteLabel && <><Grid item xs={12} sm={9}>
                                <Box marginBottom="10px" marginTop="10px">
                                    <Typography variant="h3" gutterBottom>How did you
                                        find us?</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <Field
                                    component={TextField}
                                    type="text"
                                    name="findUs"
                                    variant="outlined"
                                    fullWidth
                                    onChange={(event) => {
                                        setFieldValue("findUs", event.target.value);
                                    }}
                                    label="How did you find us?"
                                    value={values.findUs}
                                    select={true}
                                    disabled={referralCodeApplied}
                                    SelectProps={{
                                        multiple: false,
                                        SelectDisplayProps: {
                                            style: {
                                                width: "100%"
                                            }
                                        }
                                    }}
                                >
                                    {
                                        foundUsOptions.map(option => {
                                            return <MenuItem key={option.value}
                                                value={option.value}>{option.label}</MenuItem>;
                                        })
                                    }
                                </Field>
                            </Grid></>}
                        </Grid>
                        {!hasSubscription && !isWhiteLabel &&
							<Grid container className={classes.spacing}>
							    <Grid item xs={12}>
							        <Typography variant="h3" gutterBottom>
										Referral Code (optional)
							        </Typography>
							    </Grid>
							    <Grid item xs={9}>
							        <RTextField
							            fullWidth
							            variant="outlined"
							            label="Add Referral Code"
							            value={referralCode}
							            disabled={referralCodeApplied}
							            onChange={(event) => setReferralCode(event.target.value.toUpperCase())}
							        />
							    </Grid>
							    <Grid item xs="auto" justify="flex-end">
							        <Button
							            variant="contained"
							            color="primary"
							            className={classes.applyBtn}
							            disabled={applyButtonDisabled || referralCodeApplied}
							            onClick={applyReferralCode.bind(null, referralCode)}
							        >Apply</Button>
							    </Grid>
							</Grid>
                        }
                        <Grid container justify="flex-start" spacing={2} className={classes.spacing}>
                            <Grid item xs={12} sm={3}>
                                <Button
                                    fullWidth
                                    color="secondary"
                                    variant="outlined"
                                    type="submit"
                                    data-testid="submitButton"
                                    onClick={()=> setIsSubmit(true)}
                                >
                                    Save
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

export default withLocation(Profile);
